import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getNotifications } from '../../../api/notifications.api';
import { queryKeys } from '../../../api/queryKeys';
import type { NotificationEntity } from '../../../types/notification.types';
import {
  NOTIFICATIONS_PAGE_SIZE,
  NotificationsPage,
  retryNotificationsQuery,
} from '../notificationsCache';
import { useNotificationsViewer } from './useNotificationsViewer';

const FIRST_PAGE = 1;

const fetchPage = async (
  page: number,
  unreadOnly: boolean
): Promise<NotificationsPage> => {
  const { data } = await getNotifications({
    page,
    limit: NOTIFICATIONS_PAGE_SIZE,
    ...(unreadOnly ? { unread: true } : {}),
  });
  const items = data?.data ?? [];

  // No total in the response: a short page is the last one.
  return {
    items,
    nextPage: items.length === NOTIFICATIONS_PAGE_SIZE ? page + 1 : undefined,
  };
};

type Options = {
  /** Only the unread ones - the full page's Unread tab. */
  unreadOnly?: boolean;
  /** Off while the dropdown is closed, so a page load costs one call, not two. */
  enabled?: boolean;
};

/**
 * The user's notifications, newest first, a page at a time.
 *
 * The socket prepends new ones and the mark actions flip `seen` in place
 * (`notificationsCache`), so this is fetched once and kept: five minutes
 * fresh, and refetched after a socket gap.
 */
export const useNotificationsList = ({
  unreadOnly = false,
  enabled = true,
}: Options = {}) => {
  const viewer = useNotificationsViewer();

  const query = useInfiniteQuery({
    queryKey: queryKeys.notifications.list(viewer.userId, unreadOnly),
    queryFn: ({ pageParam }) => fetchPage(pageParam, unreadOnly),
    getNextPageParam: (last) => last.nextPage,
    initialPageParam: FIRST_PAGE,
    enabled: enabled && viewer.enabled,
    staleTime: 5 * 60 * 1000,
    retry: retryNotificationsQuery,
  });

  /* By id, first one wins: a notification prepended by the socket shifts the
     offset pages by one, so the next page fetched repeats a row this one
     already has. */
  const items = useMemo<NotificationEntity[]>(() => {
    const seen = new Set<number>();
    return (query.data?.pages.flatMap((page) => page.items) ?? []).filter(
      (item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      }
    );
  }, [query.data]);

  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
};
